import axios from "axios";

const api = axios.create({
	baseURL: process.env.REACT_APP_BACKEND_URL,
	withCredentials: true,
});

export const openApi = axios.create({
	baseURL: process.env.REACT_APP_BACKEND_URL
});

// ============================================================
// Interceptors registrados UMA ÚNICA VEZ no nível do módulo
// Nunca acumulam, nunca têm race condition com React
// ============================================================

// Callback que o useAuth pode setar para reagir a 401
let onUnauthorized = null;
export const setOnUnauthorized = (cb) => { onUnauthorized = cb; };

// Promesa que nunca resuelve — evita unhandled rejections en componentes
// que no atrapan errores cuando la sesión expira o se cierra
const pendingForever = () => new Promise(() => {});

const handleSessionExpired = () => {
  localStorage.removeItem("token");
  api.defaults.headers.Authorization = undefined;
  if (onUnauthorized) onUnauthorized();
};

// REQUEST: injeta token do localStorage em toda requisição
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers = config.headers ?? {};
      try {
        config.headers["Authorization"] = `Bearer ${JSON.parse(token)}`;
      } catch {
        config.headers["Authorization"] = `Bearer ${token}`;
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// RESPONSE: refresh em 403, limpa sessão em 401
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error?.config;

    // Estos endpoints manejan sus propios errores — rechazar normalmente
    if (
      originalRequest?.url?.includes("/auth/refresh_token") ||
      originalRequest?.url?.includes("/auth/login")
    ) {
      return Promise.reject(error);
    }

    // Logout retornó 401 (token ya invalidado) — tratar como éxito silencioso
    if (originalRequest?.url?.includes("/auth/logout")) {
      return pendingForever();
    }

    // 403 → tentar refresh token
    if (error?.response?.status === 403 && originalRequest && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers["Authorization"] = `Bearer ${token}`;
          return api(originalRequest);
        }).catch(() => pendingForever());
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const { data } = await api.post("/auth/refresh_token");
        if (data?.token) {
          localStorage.setItem("token", JSON.stringify(data.token));
          api.defaults.headers.Authorization = `Bearer ${data.token}`;
          processQueue(null, data.token);
          return api(originalRequest);
        }
      } catch (refreshErr) {
        // Vaciar la cola con promesas que nunca resuelven
        failedQueue.forEach(prom => prom.resolve(pendingForever()));
        failedQueue = [];
        handleSessionExpired();
        return pendingForever();
      } finally {
        isRefreshing = false;
      }
    }

    // 401 → sessão expirada
    if (error?.response?.status === 401) {
      handleSessionExpired();
      return pendingForever();
    }

    return Promise.reject(error);
  }
);

export default api;
